import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { 
  Package, 
  TrendingUp, 
  TrendingDown, 
  Target,
  BarChart3,
  Loader2,
  ArrowUp,
  DollarSign,
  Percent,
  PieChart,
  Activity,
  ShoppingCart,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  PanelRightClose,
  PanelRight
} from 'lucide-react';

// P&L Insights Panel Component - Sticky Side Panel
const PLInsightsPanel = ({ pnl, breakeven, data, formatCurrency, isCollapsed, onToggle }) => {
  const revenue = pnl?.revenue?.total || 0;
  const cogs = pnl?.cogs || 0;
  const adSpend = pnl?.ad_spend || 0;
  const adSpendSource = pnl?.ad_spend_source || 'none';
  const opEx = pnl?.operating_expenses || 0;
  const netMargin = pnl?.net_margin || 0;
  const orderCount = data?.order_count || 1;
  
  // Calculate percentages
  const cogsPercent = revenue > 0 ? (cogs / revenue * 100) : 0;
  const cogsPerDollar = revenue > 0 ? (cogs / revenue) : 0;
  const adSpendPercent = revenue > 0 ? (adSpend / revenue * 100) : 0;
  const opExPercent = revenue > 0 ? (opEx / revenue * 100) : 0;
  const revenuePerUnit = orderCount > 0 ? (revenue / orderCount) : 0;
  
  // COGS health
  const getCogsHealth = () => {
    if (cogsPercent < 25) return { dot: '🟢', label: 'Excellent margins', color: 'text-emerald-400', bg: 'bg-emerald-500/10' };
    if (cogsPercent < 40) return { dot: '🔵', label: 'Healthy', color: 'text-blue-400', bg: 'bg-blue-500/10' };
    if (cogsPercent < 60) return { dot: '🟡', label: 'Moderate', color: 'text-amber-400', bg: 'bg-amber-500/10' };
    return { dot: '🔴', label: 'High cost structure', color: 'text-red-400', bg: 'bg-red-500/10' };
  };
  
  // Ad Spend health
  const getAdSpendHealth = () => {
    if (adSpendPercent < 10) return { dot: '🟢', label: 'Efficient', color: 'text-emerald-400', bg: 'bg-emerald-500/10' };
    if (adSpendPercent < 20) return { dot: '🔵', label: 'Normal for D2C', color: 'text-blue-400', bg: 'bg-blue-500/10' };
    if (adSpendPercent < 30) return { dot: '🟡', label: 'Heavy spend', color: 'text-amber-400', bg: 'bg-amber-500/10' };
    return { dot: '🔴', label: 'Unsustainable', color: 'text-red-400', bg: 'bg-red-500/10' };
  };
  
  // OpEx health
  const getOpExHealth = () => {
    if (opExPercent < 20) return { dot: '🟢', label: 'Lean operation', color: 'text-emerald-400', bg: 'bg-emerald-500/10' };
    if (opExPercent < 35) return { dot: '🔵', label: 'Normal', color: 'text-blue-400', bg: 'bg-blue-500/10' };
    return { dot: '🟡', label: 'Review costs', color: 'text-amber-400', bg: 'bg-amber-500/10' };
  };
  
  // Profit health
  const getProfitHealth = () => {
    if (netMargin > 30) return { dot: '🟢', label: 'Strong profitability', color: 'text-emerald-400', bg: 'bg-emerald-500/10' };
    if (netMargin > 20) return { dot: '🔵', label: 'Healthy', color: 'text-blue-400', bg: 'bg-blue-500/10' };
    if (netMargin > 10) return { dot: '🟡', label: 'Thin margins', color: 'text-amber-400', bg: 'bg-amber-500/10' };
    return { dot: '🔴', label: 'At risk', color: 'text-red-400', bg: 'bg-red-500/10' };
  };
  
  const cogsHealth = getCogsHealth();
  const adHealth = getAdSpendHealth();
  const opExHealth = getOpExHealth();
  const profitHealth = getProfitHealth();
  
  // Break-even status
  const breakevenExceeded = breakeven?.progress_percent >= 100;
  const breakevenGap = breakeven?.gap_amount || 0;
  const dailyTarget = breakeven?.daily_target || 0;
  
  // Calculate days ahead/behind (rough estimate)
  const daysInMonth = 30;
  const currentRevenue = revenue;
  const targetRevenue = breakeven?.breakeven_revenue || 0;
  const daysAhead = targetRevenue > 0 && dailyTarget > 0 
    ? Math.round((currentRevenue - targetRevenue) / dailyTarget) 
    : 0;

  return (
    <div className="bg-[#1a2744] rounded-lg border border-gray-700/50 sticky top-4">
      {/* Header with collapse toggle */}
      <div className="flex items-center justify-between py-2 px-3 border-b border-gray-700/50">
        <div className="flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-xs font-medium text-white">P&L Insights</span>
        </div>
        <button
          onClick={onToggle}
          className="p-1 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded transition-colors"
          title={isCollapsed ? "Expand panel" : "Collapse panel"}
        >
          {isCollapsed ? (
            <PanelRight className="w-3.5 h-3.5" />
          ) : (
            <PanelRightClose className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
      
      {!isCollapsed && (
        <div className="px-3 pb-3 pt-2">
          <div className="space-y-2.5">
            {/* COGS Breakdown */}
            <div className={`p-2 rounded-lg ${cogsHealth.bg}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <PieChart className="w-3 h-3 text-gray-400" />
                <span className="text-[9px] text-gray-400 uppercase tracking-wide">COGS Breakdown</span>
              </div>
              <p className="text-[11px] text-gray-300">
                Your blended COGS is <span className={`font-mono font-medium ${cogsHealth.color}`}>{cogsPercent.toFixed(1)}%</span> of revenue
                <span className="text-gray-500"> (${cogsPerDollar.toFixed(2)} per $1 sold)</span>
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">{cogsHealth.dot} {cogsHealth.label}</p>
            </div>
            
            {/* Ad Spend Ratio */}
            <div className={`p-2 rounded-lg ${adHealth.bg}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className="w-3 h-3 text-gray-400" />
                <span className="text-[9px] text-gray-400 uppercase tracking-wide">Ad Spend Ratio</span>
                {adSpendSource !== 'none' && (
                  <span className={`text-[8px] px-1 py-0.5 rounded ${
                    adSpendSource === 'campaigns' 
                      ? 'bg-emerald-500/30 text-emerald-400' 
                      : 'bg-gray-500/30 text-gray-400'
                  }`}>
                    {adSpendSource === 'campaigns' ? 'PPC' : 'Manual'}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-gray-300">
                Advertising is <span className={`font-mono font-medium ${adHealth.color}`}>{adSpendPercent.toFixed(1)}%</span> of revenue
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">{adHealth.dot} {adHealth.label}</p>
            </div>
            
            {/* OpEx Load */}
            <div className={`p-2 rounded-lg ${opExHealth.bg}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <Activity className="w-3 h-3 text-gray-400" />
                <span className="text-[9px] text-gray-400 uppercase tracking-wide">OpEx Load</span>
              </div>
              <p className="text-[11px] text-gray-300">
                Operating expenses are <span className={`font-mono font-medium ${opExHealth.color}`}>{opExPercent.toFixed(1)}%</span> of revenue
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">{opExHealth.dot} {opExHealth.label}</p>
            </div>
            
            {/* Profit Health */}
            <div className={`p-2 rounded-lg ${profitHealth.bg}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3 h-3 text-gray-400" />
                <span className="text-[9px] text-gray-400 uppercase tracking-wide">Profit Health</span>
              </div>
              <p className="text-[11px] text-gray-300">
                Net margin: <span className={`font-mono font-medium ${profitHealth.color}`}>{netMargin.toFixed(1)}%</span>
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">{profitHealth.dot} {profitHealth.label}</p>
            </div>
            
            {/* Revenue per Unit */}
            <div className="p-2 rounded-lg bg-[#0f1729]">
              <div className="flex items-center gap-1.5 mb-1">
                <ShoppingCart className="w-3 h-3 text-gray-400" />
                <span className="text-[9px] text-gray-400 uppercase tracking-wide">Revenue per Unit</span>
              </div>
              <p className="text-[11px] text-gray-300">
                Avg revenue per unit sold: <span className="font-mono font-medium text-white">${revenuePerUnit.toFixed(2)}</span>
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">{orderCount.toLocaleString()} orders this period</p>
            </div>
            
            {/* Break-even Status */}
            <div className={`p-2 rounded-lg ${breakevenExceeded ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
              <div className="flex items-center gap-1.5 mb-1">
                <CheckCircle className="w-3 h-3 text-gray-400" />
                <span className="text-[9px] text-gray-400 uppercase tracking-wide">Break-even Status</span>
              </div>
              {breakevenExceeded ? (
                <p className="text-[11px] text-emerald-400">
                  🟢 Break-even exceeded by <span className="font-mono font-medium">{formatCurrency(Math.abs(breakevenGap * daysInMonth))}</span>
                  {daysAhead > 0 && <span className="text-emerald-300"> ({daysAhead} days ahead of pace)</span>}
                </p>
              ) : (
                <p className="text-[11px] text-amber-400">
                  🟡 <span className="font-mono font-medium">{formatCurrency(Math.abs(breakevenGap * daysInMonth))}</span> remaining to break even
                  <span className="text-amber-300"> (need {formatCurrency(Math.abs(breakevenGap))}/day)</span>
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const OverviewTab = ({ data, loading, formatCurrency, selectedPeriod }) => {
  const [insightsPanelCollapsed, setInsightsPanelCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
        <span className="ml-2 text-gray-400 text-sm">Loading...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-400">
        <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No data available</p>
        <p className="text-xs mt-1">Add inventory and expenses to start</p>
      </div>
    );
  }

  const { metrics, pnl, breakeven } = data;

  const getMarginColor = (value) => {
    if (value > 30) return 'text-emerald-400';
    if (value > 15) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getNetMarginColor = (value) => {
    if (value > 10) return 'text-emerald-400';
    if (value >= 0) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="flex gap-4">
      {/* Left Side - Main Content (P&L Statement, Break-even) */}
      <div className={`flex-1 space-y-4 ${insightsPanelCollapsed ? 'max-w-6xl' : 'max-w-4xl'}`}>
        {/* 4 Compact Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Inventory Value */}
          <div className="bg-[#1a2744] rounded-lg p-3 border border-gray-700/50">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Inventory</p>
                <p className="text-xl font-bold text-white mt-0.5">
                  {formatCurrency(metrics.inventory_value)}
                </p>
                <p className="text-[10px] text-gray-500">
                  {metrics.inventory_units.toLocaleString()} units
                </p>
              </div>
              <Package className="w-4 h-4 text-blue-400" />
            </div>
          </div>

          {/* Gross Margin */}
          <div className="bg-[#1a2744] rounded-lg p-3 border border-gray-700/50">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Gross Margin</p>
                <p className={`text-xl font-bold mt-0.5 ${getMarginColor(metrics.gross_margin)}`}>
                  {metrics.gross_margin.toFixed(1)}%
                </p>
                <p className="text-[10px] text-gray-500">
                  {formatCurrency(metrics.gross_profit)} profit
                </p>
              </div>
              <TrendingUp className="w-4 h-4 text-emerald-400" />
            </div>
          </div>

          {/* Net Margin */}
          <div className="bg-[#1a2744] rounded-lg p-3 border border-gray-700/50">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Net Margin</p>
                <p className={`text-xl font-bold mt-0.5 ${getNetMarginColor(metrics.net_margin)}`}>
                  {metrics.net_margin.toFixed(1)}%
                </p>
                <p className="text-[10px] text-gray-500">
                  {metrics.net_profit >= 0 ? formatCurrency(metrics.net_profit) : `-${formatCurrency(Math.abs(metrics.net_profit))}`}
                </p>
              </div>
              {metrics.net_margin >= 0 ? (
                <TrendingUp className="w-4 h-4 text-emerald-400" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-400" />
              )}
            </div>
          </div>

          {/* Break-even Gap */}
          <div className="bg-[#1a2744] rounded-lg p-3 border border-gray-700/50">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-400 uppercase tracking-wide">Break-even Gap</p>
                <p className="text-xl font-bold text-amber-400 mt-0.5">
                  {formatCurrency(breakeven.gap_amount)}/d
                </p>
                <p className="text-[10px] text-gray-500">needed daily</p>
              </div>
              <Target className="w-4 h-4 text-amber-400" />
            </div>
          </div>
        </div>

        {/* P&L and Break-even - Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Monthly P&L Summary - Compact */}
          <Card className="bg-[#1a2744] border-gray-700/50">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs flex items-center gap-1.5 text-white">
                <BarChart3 className="w-3.5 h-3.5 text-emerald-400" />
                Monthly P&L Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 pt-0 space-y-1.5">
              {/* Revenue */}
              <div className="flex justify-between items-center py-1.5 border-b border-gray-700/50">
                <span className="text-xs text-gray-300">Revenue</span>
                <div className="text-right">
                  <span className="font-mono text-sm text-white">{formatCurrency(pnl.revenue.total)}</span>
                  {pnl.revenue.total > 0 && (
                    <p className="text-[9px] text-gray-500">
                      E-com: {formatCurrency(pnl.revenue.ecommerce)} | Retail: {formatCurrency(pnl.revenue.retail)}
                    </p>
                  )}
                </div>
              </div>

              {/* COGS */}
              <div className="flex justify-between items-center py-1.5 border-b border-gray-700/50">
                <span className="text-xs text-gray-300">Less: COGS ({pnl.cogs_percent.toFixed(0)}%)</span>
                <span className="font-mono text-sm text-red-400">-{formatCurrency(pnl.cogs)}</span>
              </div>

              {/* Gross Profit */}
              <div className="flex justify-between items-center py-1.5 border-b border-gray-700/50 bg-gray-800/30 -mx-3 px-3">
                <span className="text-xs text-white font-medium">Gross Profit</span>
                <div className="text-right">
                  <span className={`font-mono text-sm ${pnl.gross_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrency(pnl.gross_profit)}
                  </span>
                  <span className="text-[9px] text-gray-500 ml-1">({pnl.gross_margin.toFixed(1)}%)</span>
                </div>
              </div>

              {/* Operating Expenses */}
              <div className="flex justify-between items-center py-1.5 border-b border-gray-700/50">
                <span className="text-xs text-gray-300">Less: Operating Expenses</span>
                <span className="font-mono text-sm text-red-400">-{formatCurrency(pnl.operating_expenses)}</span>
              </div>

              {/* Ad Spend */}
              <div className="flex justify-between items-center py-1.5 border-b border-gray-700/50">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-300">Less: Ad Spend</span>
                  {pnl.ad_spend_source && pnl.ad_spend_source !== 'none' && (
                    <span className={`text-[8px] px-1 py-0.5 rounded ${
                      pnl.ad_spend_source === 'campaigns' 
                        ? 'bg-emerald-500/20 text-emerald-400' 
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {pnl.ad_spend_source === 'campaigns' ? 'PPC' : 'Manual'}
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <span className="font-mono text-sm text-red-400">-{formatCurrency(pnl.ad_spend)}</span>
                  {pnl.ppc_metrics && pnl.ppc_metrics.acos > 0 && (
                    <p className="text-[9px] text-gray-500">
                      ACOS: {pnl.ppc_metrics.acos}%
                    </p>
                  )}
                </div>
              </div>

              {/* Net Profit */}
              <div className="flex justify-between items-center py-2 bg-gradient-to-r from-gray-800/50 to-transparent -mx-3 px-3 rounded">
                <span className="text-xs text-white font-bold">Net Profit</span>
                <div className="text-right">
                  <span className={`font-mono text-base font-bold ${pnl.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {pnl.net_profit >= 0 ? '' : '-'}{formatCurrency(Math.abs(pnl.net_profit))}
                  </span>
                  <span className={`text-[9px] ml-1 ${pnl.net_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    ({pnl.net_margin.toFixed(1)}%)
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Break-even Analysis - Compact */}
          <Card className="bg-[#1a2744] border-gray-700/50">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs flex items-center gap-1.5 text-white">
                <Target className="w-3.5 h-3.5 text-amber-400" />
                Break-even Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3 pt-0 space-y-3">
              {/* Key Metrics Row */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-[#0f1729] rounded p-2 text-center">
                  <p className="text-[9px] text-gray-400">Fixed Costs</p>
                  <p className="font-mono text-sm text-white">{formatCurrency(breakeven.monthly_fixed_costs)}</p>
                  <p className="text-[8px] text-gray-500">OpEx+Ads</p>
                </div>
                <div className="bg-[#0f1729] rounded p-2 text-center">
                  <p className="text-[9px] text-gray-400">Gross Margin</p>
                  <p className={`font-mono text-sm ${getMarginColor(breakeven.gross_margin)}`}>
                    {breakeven.gross_margin.toFixed(1)}%
                  </p>
                </div>
                <div className="bg-[#0f1729] rounded p-2 text-center">
                  <p className="text-[9px] text-gray-400">Break-even</p>
                  <p className="font-mono text-sm text-amber-400">{formatCurrency(breakeven.breakeven_revenue)}</p>
                  <p className="text-[8px] text-gray-500">/month</p>
                </div>
              </div>

              {/* Progress Bar - Thin */}
              <div className="space-y-1">
                <div className="flex justify-between text-[10px]">
                  <span className="text-gray-400">Progress</span>
                  <span className={breakeven.progress_percent >= 100 ? 'text-emerald-400' : 'text-amber-400'}>
                    {breakeven.progress_percent.toFixed(0)}%
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${
                      breakeven.progress_percent >= 100 
                        ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' 
                        : 'bg-gradient-to-r from-amber-500 to-amber-400'
                    }`}
                    style={{ width: `${Math.min(breakeven.progress_percent, 100)}%` }}
                  />
                </div>
              </div>

              {/* Daily Metrics */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-[#0f1729] rounded p-2 text-center border border-gray-700/50">
                  <p className="text-[9px] text-gray-400">Daily Target</p>
                  <p className="font-mono text-sm text-white">{formatCurrency(breakeven.daily_target)}</p>
                </div>
                <div className="bg-[#0f1729] rounded p-2 text-center border border-gray-700/50">
                  <p className="text-[9px] text-gray-400">Current Avg</p>
                  <p className="font-mono text-sm text-blue-400">{formatCurrency(breakeven.current_daily_avg)}</p>
                </div>
                <div className="bg-[#0f1729] rounded p-2 text-center border border-amber-500/30">
                  <p className="text-[9px] text-gray-400">Gap</p>
                  <p className="font-mono text-sm text-amber-400">{formatCurrency(breakeven.gap_amount)}/d</p>
                </div>
              </div>

              {/* Status Message - Compact */}
              <div className={`p-2 rounded text-xs ${
                breakeven.progress_percent >= 100 
                  ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' 
                  : 'bg-amber-500/10 border border-amber-500/30 text-amber-400'
              }`}>
                {breakeven.progress_percent >= 100 ? (
                  <span className="flex items-center gap-1">
                    <ArrowUp className="w-3 h-3" />
                    Break-even exceeded!
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    Need +{formatCurrency(breakeven.gap_amount)}/day to break even
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Side - P&L Insights Panel (Sticky) */}
      <div className={`w-72 flex-shrink-0 hidden xl:block transition-all duration-300 ${insightsPanelCollapsed ? 'w-12' : 'w-72'}`}>
        <PLInsightsPanel 
          pnl={pnl} 
          breakeven={breakeven} 
          data={data}
          formatCurrency={formatCurrency}
          isCollapsed={insightsPanelCollapsed}
          onToggle={() => setInsightsPanelCollapsed(!insightsPanelCollapsed)}
        />
      </div>

      {/* Collapsed toggle button for xl screens when panel is hidden */}
      {insightsPanelCollapsed && (
        <div className="hidden xl:block">
          <button
            onClick={() => setInsightsPanelCollapsed(false)}
            className="sticky top-4 p-2 bg-[#1a2744] border border-gray-700/50 rounded-lg text-gray-400 hover:text-white hover:bg-[#1e2d4d] transition-colors"
            title="Show P&L Insights"
          >
            <PanelRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default OverviewTab;
